import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoArrowUndoOutline } from 'react-icons/io5';
import { MdShare, MdDownload } from 'react-icons/md';
import './PdfViewer.css';

const PdfViewer = ({ pdfUrl, title = 'PDF Document' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (pdfUrl) {
      const timer = setTimeout(() => {
        if (isLoading) console.log('Loading document is taking longer than usual...');
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [pdfUrl, isLoading]);

  const handleLoadSuccess = () => { setIsLoading(false); setError(null); };
  const handleLoadError = () => { setIsLoading(false); setError('Unable to load document. Please try again later.'); };

  const downloadPDF = async (url, fileName) => {
    try {
      setIsDownloading(true);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to download PDF');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const sharePDFViaEmail = (url) => {
    const subject = 'Check out this PDF!';
    const body = `${title} pdf: ${url}`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  const Header = () => (
    <header className="pdf-viewer-header">
      <button className="pdf-icon-btn" onClick={() => navigate(-1)}>
        <IoArrowUndoOutline size={22} color="#1a2134" />
      </button>
      <h1 className="pdf-header-title">{title}</h1>
      <button className="pdf-icon-btn" onClick={() => sharePDFViaEmail(pdfUrl)}>
        <MdShare size={22} color="#1a2134" />
      </button>
    </header>
  );

  if (error) {
    return (
      <div className="pdf-viewer-container">
        <Header />
        <div className="error-container">
          <div className="error-content">
            <div className="error-icon">⚠️</div>
            <h2>Failed to load PDF</h2>
            <p>{error}</p>
            <button className="retry-button" onClick={() => { setError(null); setIsLoading(true); }}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-container">
      <Header />
      <div className="pdf-content">
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading PDF...</p>
          </div>
        )}
        {pdfUrl && (
          <iframe
            src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            title={title}
            width="100%"
            height="100%"
            onLoad={handleLoadSuccess}
            onError={handleLoadError}
            style={{ border: 'none' }}
          />
        )}
      </div>
      <button
        className={`download-fab ${isDownloading ? 'downloading' : ''}`}
        onClick={() => downloadPDF(pdfUrl, 'document.pdf')}
        disabled={isDownloading}
      >
        {isDownloading ? <div className="download-spinner"></div> : <MdDownload size={24} />}
      </button>
    </div>
  );
};

export default PdfViewer;